<script lang="ts">
	import { slide } from 'svelte/transition';
	import IconExpandMore from 'virtual:icons/material-symbols/expand-more-rounded';
	import IconCheck from 'virtual:icons/material-symbols/check-rounded';
	import IconError from 'virtual:icons/material-symbols/error-outline-rounded';
	import { t } from './i18n/tranlsator.svelte';
	import type { I18nKey } from './i18n/translations';
	import type { ToolCall } from '#src/types/chat.ts';
	import ToolTypeIcon from './ToolTypeIcon.svelte';
	import LoadingSpinner from './LoadingSpinner.svelte';
	import { tooltip } from '@astlai/common/tooltip/tooltip.svelte.ts';
	import Markdown from '#lib/Markdown.svelte';

	interface ToolMeta {
		labelKey: I18nKey | null;
		label?: string;
	}

	const TOOL_META: Record<string, ToolMeta> = {
		vector_search: { labelKey: 'chat.tool.vectorSearch' },
		keyword_search: { labelKey: 'chat.tool.keywordSearch' },
		get_document: { labelKey: 'chat.tool.document' },
		get_neighboring_chunks: { labelKey: 'chat.tool.context' },
		web_search: { labelKey: 'chat.tool.webSearch' },
		fetch_url: { labelKey: 'chat.tool.website' },
		query_bundestag: { labelKey: 'chat.tool.parliament' },
		query_parliament: { labelKey: 'chat.tool.parliament' },
		query_parlament: { labelKey: 'chat.tool.parliament' },
		search_lobbyregister: { labelKey: 'chat.tool.lobbyregister' },
		get_lobbyregister_entry: { labelKey: 'chat.tool.lobbyregisterEntry' },
		search_abgeordnetenwatch_politicians: { labelKey: 'chat.tool.politicians' },
		search_abgeordnetenwatch_polls: { labelKey: 'chat.tool.votes' },
		query_kleine_anfragen: { labelKey: 'chat.tool.kleineAnfragen' },
		search_fragdenstaat: { labelKey: 'chat.tool.fragdenstaat' },
		mark_briefing_status: { labelKey: 'chat.tool.briefingStatus' },
		generate_image: { labelKey: 'chat.tool.generateImage' }
	};

	interface Props {
		toolCalls?: ToolCall[];
		reasoningContent?: string;
		isLive?: boolean;
	}

	let { toolCalls = [], reasoningContent, isLive = false }: Props = $props();

	let expanded = $state(false);
	function toggle() {
		expanded = !expanded;
	}

	// Active tab — defaults to 'tools' if tools exist, else 'reasoning'
	let _activeTab: 'tools' | 'reasoning' | null = $state(null);
	const activeTab = $derived(_activeTab ?? (toolCalls.length ? 'tools' : 'reasoning'));
	function setTab(tab: 'tools' | 'reasoning') {
		_activeTab = tab;
	}

	function isDone(tool: ToolCall) {
		return tool.status === 'completed' || tool.status === 'error';
	}

	function isActive(tool: ToolCall) {
		return isLive && tool.status === 'running';
	}

	function metaFor(tool: ToolCall): ToolMeta {
		return TOOL_META[tool.name] ?? { labelKey: null, label: tool.name };
	}
</script>

<div class="research-block">
	<button class="toggle" onclick={toggle} aria-expanded={expanded}>
		<div class="chevron" class:rotated={expanded}>
			<IconExpandMore />
		</div>
		<span>{t('chat.toolCalls')}</span>
	</button>

	{#if expanded}
		<div class="block-content" transition:slide={{ duration: 200 }}>
			{#if toolCalls.length && reasoningContent}
				<div class="tabs" transition:slide={{ duration: 200 }}>
					<button
						class="tab compact stealth"
						class:active={activeTab === 'tools'}
						onclick={() => setTab('tools')}>{t('chat.tab.toolCalls')} ({toolCalls.length})</button
					>
					<button
						class="tab compact stealth"
						class:active={activeTab === 'reasoning'}
						onclick={() => setTab('reasoning')}>{t('chat.tab.reasoning')}</button
					>
				</div>
			{/if}

			{#if activeTab === 'tools' && toolCalls.length}
				<ul class="tool-list">
					{#each toolCalls as tool (tool.call_id)}
						{@const meta = metaFor(tool)}
						{@const done = isDone(tool)}
						{@const active = isActive(tool)}
						<li class="tool-card">
							<div class="card-left">
								<div
									class="type-icon"
									use:tooltip
									aria-label={meta.labelKey ? t(meta.labelKey) : meta.label}
								>
									<ToolTypeIcon name={tool.name} />
								</div>
							</div>
							<span class="description"
								>{tool.description || (meta.labelKey ? t(meta.labelKey) : meta.label)}</span
							>
							<div class="card-right">
								{#if done && tool.result_count != null && tool.result_count > 0}
									<span class="result-count"
										>{tool.result_count}
										{tool.result_count === 1 ? t('chat.tool.result') : t('chat.tool.results')}</span
									>
								{/if}
								<div class="status">
									{#if tool.status === 'error'}
										<div class="error-icon" use:tooltip aria-label={tool.error ?? 'Tool error'}>
											<IconError />
										</div>
									{:else if done}
										<IconCheck />
									{:else if active}
										<LoadingSpinner />
									{/if}
								</div>
							</div>
						</li>
					{/each}
				</ul>
			{:else if activeTab === 'reasoning' && reasoningContent}
				<div class="reasoning-content">
					<Markdown source={reasoningContent} />
				</div>
			{/if}
		</div>
	{/if}
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.research-block {
		.toggle {
			@include lib.flex-row();
			@include lib.flex-align(flex-start, center);
			gap: 0.25rem;
			padding: 0.25rem 0.5rem 0.25rem 0;
			background: transparent;
			border: none;
			color: var(--text-tertiary);
			font-size: 0.75rem;
			cursor: pointer;

			&:hover {
				color: var(--text-secondary);
			}

			.chevron {
				flex-shrink: 0;
				transition: transform 200ms ease;
				@include lib.flex-row();
				@include lib.flex-align();

				&.rotated {
					transform: rotate(180deg);
				}
			}
		}

		.block-content {
			@include lib.flex-column();
			gap: 0.5rem;

			.tabs {
				@include lib.flex-row();
				gap: 0.25rem;

				.tab {
					color: var(--text-tertiary);
					font-size: 0.75rem;
					&.active {
						background: var(--surface-3);
						color: var(--text-secondary);
					}
				}
			}

			.tool-list {
				list-style: none;
				margin: 0;
				padding: 0;
				@include lib.flex-column();
				border: 1px solid var(--border-subtle);
				border-radius: 0.5rem;
				overflow: hidden;

				.tool-card {
					@include lib.flex-row();
					@include lib.flex-align(flex-start, center);
					gap: 0.5rem;
					padding: 0.3rem 0.625rem;
					background: transparent;
					border-bottom: 1px solid var(--border-subtle);
					font-size: 0.75rem;
					color: var(--text-tertiary);

					&:last-child {
						border-bottom: none;
					}

					.card-left {
						@include lib.flex-row();
						@include lib.flex-align(flex-start, center);
						flex-shrink: 0;

						.type-icon {
							font-size: 0.875rem;
							cursor: default;
							@include lib.flex-row();
							@include lib.flex-align();
						}
					}

					.description {
						flex: 1;
						overflow: hidden;
						text-overflow: ellipsis;
						white-space: nowrap;
						color: var(--text-tertiary);
						min-width: 0;
					}

					.card-right {
						@include lib.flex-row();
						@include lib.flex-align(flex-end, center);
						gap: 0.375rem;
						flex-shrink: 0;
						margin-left: auto;

						.result-count {
							color: var(--text-tertiary);
						}

						.status {
							font-size: 0.875rem;
							width: 1rem;
							@include lib.flex-row();
							@include lib.flex-align();

							.error-icon {
								color: var(--text-warning);
								cursor: default;
							}
						}
					}
				}
			}

			.reasoning-content {
				padding: 0.25rem 0.5rem;
				border-left: 2px solid var(--border-subtle);
				margin-left: 0.25rem;
				color: var(--text-tertiary);
				font-size: 0.75rem;
				line-height: 1.5;
			}
		}
	}
</style>
